import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '@/store/base-query';

export interface CreateApiKeyRequest {
  name?: string;
  expires_at?: string;
}

export interface CreateApiKeyResponse {
  api_key: string;
  created_at: string;
  expires_at?: string;
}

export interface ApiKeyInfo {
  id: string;
  name?: string;
  key_prefix: string;
  key_last4: string;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  request_count?: number;
  is_active: boolean;
}

export interface RevokeApiKeyResponse {
  success: boolean;
  message: string;
}

export interface ApiKeyRequestLogInfo {
  id: string;
  method: string;
  path: string;
  status_code: number;
  duration_ms: number;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export const apiKeysApi = createApi({
  reducerPath: 'apiKeysApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ApiKeys'],
  endpoints: (builder) => ({
    createApiKey: builder.mutation<CreateApiKeyResponse, CreateApiKeyRequest>({
      query: (body) => ({
        url: '/api-keys',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    listApiKeys: builder.query<ApiKeyInfo[], void>({
      query: () => '/api-keys',
      providesTags: ['ApiKeys'],
    }),
    revokeApiKey: builder.mutation<RevokeApiKeyResponse, string>({
      query: (keyId) => ({
        url: `/api-keys/${keyId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    revokeAllApiKeys: builder.mutation<RevokeApiKeyResponse, void>({
      query: () => ({
        url: '/api-keys/revoke-all',
        method: 'POST',
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    listApiKeyRequests: builder.query<
      ApiKeyRequestLogInfo[],
      { keyId: string; limit?: number }
    >({
      query: ({ keyId, limit = 100 }) =>
        `/api-keys/${keyId}/requests?limit=${limit}`,
      providesTags: ['ApiKeys'],
    }),
  }),
});

export const {
  useCreateApiKeyMutation,
  useListApiKeysQuery,
  useListApiKeyRequestsQuery,
  useRevokeApiKeyMutation,
  useRevokeAllApiKeysMutation,
} = apiKeysApi;
